// Client helpers for KPI CRUD + metrics catalog — sprint MP59.

import type { Kpi, KpiMetric, KpiWithValue } from '@cyber-aware/shared-types';
import { jsonOrThrow } from './apiError';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

/** Static metrics catalog. Cached by the SPA after first fetch
 *  since it's the same for every org. */
export async function listKpiMetrics(idToken: string): Promise<KpiMetric[]> {
  const res = await fetch(`${API_URL}/v1/kpi-metrics`, {
    headers: { authorization: `Bearer ${idToken}` },
  });
  const body = await jsonOrThrow(res);
  return body.metrics ?? [];
}

export async function listKpis(idToken: string, orgId: string): Promise<KpiWithValue[]> {
  const res = await fetch(`${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpis`, {
    headers: { authorization: `Bearer ${idToken}` },
  });
  const body = await jsonOrThrow(res);
  return body.kpis ?? [];
}

export interface CreateKpiInput {
  name: string;
  description?: string;
  metricKey: string;
  target: number;
  polarity?: 'higher-better' | 'lower-better';
}

export async function createKpi(
  idToken: string,
  orgId: string,
  input: CreateKpiInput,
): Promise<Kpi> {
  const res = await fetch(`${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpis`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${idToken}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  const body = await jsonOrThrow(res);
  return body.kpi;
}

export async function deleteKpi(
  idToken: string,
  orgId: string,
  kpiId: string,
): Promise<void> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpis/${encodeURIComponent(kpiId)}`,
    { method: 'DELETE', headers: { authorization: `Bearer ${idToken}` } },
  );
  await jsonOrThrow(res);
}

// ---------- MP64: KPI dashboards ----------

import type { KpiDashboard, KpiDashboardWithKpis } from '@cyber-aware/shared-types';

export async function listKpiDashboards(idToken: string, orgId: string): Promise<KpiDashboard[]> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpi-dashboards`,
    { headers: { authorization: `Bearer ${idToken}` } },
  );
  const body = await jsonOrThrow(res);
  return body.dashboards ?? [];
}

export async function getKpiDashboard(
  idToken: string,
  orgId: string,
  dashboardId: string,
): Promise<KpiDashboardWithKpis> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpi-dashboards/${encodeURIComponent(dashboardId)}`,
    { headers: { authorization: `Bearer ${idToken}` } },
  );
  const body = await jsonOrThrow(res);
  return body.dashboard;
}

export interface CreateDashboardInput {
  name: string;
  description?: string;
  kpiIds?: string[];
}

export async function createKpiDashboard(
  idToken: string,
  orgId: string,
  input: CreateDashboardInput,
): Promise<KpiDashboard> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpi-dashboards`,
    {
      method: 'POST',
      headers: {
        authorization: `Bearer ${idToken}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify(input),
    },
  );
  const body = await jsonOrThrow(res);
  return body.dashboard;
}

export async function updateKpiDashboard(
  idToken: string,
  orgId: string,
  dashboardId: string,
  patch: { name?: string; description?: string; kpiIds?: string[] },
): Promise<KpiDashboard> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpi-dashboards/${encodeURIComponent(dashboardId)}`,
    {
      method: 'PATCH',
      headers: {
        authorization: `Bearer ${idToken}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify(patch),
    },
  );
  const body = await jsonOrThrow(res);
  return body.dashboard;
}

export async function deleteKpiDashboard(
  idToken: string,
  orgId: string,
  dashboardId: string,
): Promise<void> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpi-dashboards/${encodeURIComponent(dashboardId)}`,
    { method: 'DELETE', headers: { authorization: `Bearer ${idToken}` } },
  );
  await jsonOrThrow(res);
}

// ---------- MP65: KPI dashboard report schedules ----------

import type { KpiDashboardReportSchedule } from '@cyber-aware/shared-types';

export async function listDashboardSchedules(
  idToken: string,
  orgId: string,
  dashboardId: string,
): Promise<KpiDashboardReportSchedule[]> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpi-dashboards/${encodeURIComponent(dashboardId)}/report-schedules`,
    { headers: { authorization: `Bearer ${idToken}` } },
  );
  const body = await jsonOrThrow(res);
  return body.schedules ?? [];
}

export interface CreateScheduleInput {
  name: string;
  cadence: 'daily' | 'weekly' | 'monthly';
  hourUtc: number;
  dayOfWeek?: number;
  dayOfMonth?: number;
  recipientMode: 'admins' | 'custom';
  customRecipients?: string[];
  enabled?: boolean;
}

export async function createDashboardSchedule(
  idToken: string,
  orgId: string,
  dashboardId: string,
  input: CreateScheduleInput,
): Promise<KpiDashboardReportSchedule> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpi-dashboards/${encodeURIComponent(dashboardId)}/report-schedules`,
    {
      method: 'POST',
      headers: {
        authorization: `Bearer ${idToken}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify(input),
    },
  );
  const body = await jsonOrThrow(res);
  return body.schedule;
}

export async function updateDashboardSchedule(
  idToken: string,
  orgId: string,
  dashboardId: string,
  scheduleId: string,
  patch: Partial<CreateScheduleInput>,
): Promise<KpiDashboardReportSchedule> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpi-dashboards/${encodeURIComponent(dashboardId)}/report-schedules/${encodeURIComponent(scheduleId)}`,
    {
      method: 'PATCH',
      headers: {
        authorization: `Bearer ${idToken}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify(patch),
    },
  );
  const body = await jsonOrThrow(res);
  return body.schedule;
}

export async function deleteDashboardSchedule(
  idToken: string,
  orgId: string,
  dashboardId: string,
  scheduleId: string,
): Promise<void> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpi-dashboards/${encodeURIComponent(dashboardId)}/report-schedules/${encodeURIComponent(scheduleId)}`,
    { method: 'DELETE', headers: { authorization: `Bearer ${idToken}` } },
  );
  await jsonOrThrow(res);
}

export async function sendDashboardReportNow(
  idToken: string,
  orgId: string,
  dashboardId: string,
  scheduleId: string,
): Promise<{ sent: number; failed: number; dashboardMissing: boolean }> {
  const res = await fetch(
    `${API_URL}/v1/orgs/${encodeURIComponent(orgId)}/kpi-dashboards/${encodeURIComponent(dashboardId)}/report-schedules/${encodeURIComponent(scheduleId)}/send-now`,
    { method: 'POST', headers: { authorization: `Bearer ${idToken}` } },
  );
  const body = await jsonOrThrow(res);
  return {
    sent: body.sent ?? 0,
    failed: body.failed ?? 0,
    dashboardMissing: body.dashboardMissing ?? false,
  };
}
